import {
  CollectDataFormItemType,
  CollectDataStepType,
  MedicalCardRequirement,
} from 'src/constants/collect-data/enums'
import { filterDeprecatedCollectDataSteps } from 'src/utils/collectData'
import type {
  BackgroundCheckStepConfig,
  BusinessEntityStepConfig,
  CollectDataFormTemplateItem,
  CollectDataTemplateStepMetadataFields,
  CollectDataTemplateStepRow,
  CollectDataTemplateStepsConfig,
  DotNumberStepConfig,
  FormStepConfig,
  InsuranceStepConfig,
  LmsTrainingStepConfig,
  McNumberStepConfig,
  MvrUploadStepConfig,
  OnboardingTemplate,
  OnboardingTemplateStep,
  SignContractStepConfig,
  SsnAndAddressStepConfig,
  StepConfig,
  TemplateFormItem,
  TextBlockFormItem,
  VehicleAndInsuranceStepConfig,
  VehicleRegistrationStepConfig,
} from 'src/interfaces/onboardingTemplate'
import {
  defaultOnboardingTemplateStepRowScalars,
  StepApprovalType,
} from 'src/interfaces/onboardingTemplate'
import { normalizeCollectDataTemplateConfigId } from './configIds'

function stepMetadata(row: CollectDataTemplateStepRow): CollectDataTemplateStepMetadataFields {
  return row.metadata ?? {}
}

/** API may return text blocks as `{ text }` or editor shape `{ content }`. */
function normalizeFormItemsFromApi(metaFormItems: unknown): TemplateFormItem[] {
  if (!Array.isArray(metaFormItems)) {
    return []
  }

  const formItems: TemplateFormItem[] = []

  for (const entry of metaFormItems) {
    if (typeof entry !== 'object' || entry === null) {
      continue
    }

    const rawFormItem = entry as Record<string, unknown>
    const id = String(rawFormItem.id ?? '')
    const type = rawFormItem.type as CollectDataFormItemType

    if (!id || type === null || type === undefined) {
      continue
    }

    if (type === CollectDataFormItemType.TextBlock) {
      let content = ''

      if (typeof rawFormItem.content === 'string') {
        content = rawFormItem.content
      } else if (typeof rawFormItem.text === 'string') {
        content = rawFormItem.text
      }

      const block: TextBlockFormItem = {
        id,
        type: CollectDataFormItemType.TextBlock,
        content: String(content),
      }

      formItems.push(block)

      continue
    }

    formItems.push(entry as TemplateFormItem)
  }

  return formItems
}

export function defaultStepConfigForType(type: CollectDataStepType): StepConfig {
  const base = { approvalType: StepApprovalType.AutoApproval, delayed: false }

  switch (type) {
    case CollectDataStepType.BGC:
    case CollectDataStepType.MVR:
      return {
        ...base,
        optional: false,
        yardstikPackage: '',
        enableYardstik: false,
        continueOnIncomplete: false,
        manualUpload: false,
        expiresInDays: 365,
      }

    case CollectDataStepType.SignContract:
      return {
        ...base,
        docusignTemplateId: '',
      }

    case CollectDataStepType.LMSTraining:
      return {
        ...base,
        lmsTrainingId: '',
        lmsTrainingName: '',
        expiresInDays: 365,
        manualUpload: false,
      }

    case CollectDataStepType.VehicleInsurance:
      return {
        ...base,
        approvalType: StepApprovalType.InternalApproval,
        disableInsuranceUpload: false,
        requireExpirationDate: true,
      }

    case CollectDataStepType.VehicleRegistration:
      return {
        ...base,
        approvalType: StepApprovalType.InternalApproval,
        requireExpirationDate: true,
      }

    case CollectDataStepType.PIICollection:
      return {
        ...base,
        optional: false,
        requireProofOfWorkAuthorization: false,
        enforceSsnCollection: false,
      }

    case CollectDataStepType.EIN:
      return {
        ...base,
        optional: false,
        maxAttempts: 0,
      }

    case CollectDataStepType.DOTNumber:
      return {
        ...base,
        optional: false,
        medicalCardRequirement: MedicalCardRequirement.Required,
      }

    case CollectDataStepType.MCNumber:
      return {
        ...base,
        optional: false,
      }

    case CollectDataStepType.Form:
      return {
        label: type,
        optional: false,
        approvalType: StepApprovalType.AutoApproval,
        name: '',
        description: '',
        maxAttempts: 0,
        formItems: [],
      }

    case CollectDataStepType.InsuranceCoverage:
      return {
        ...base,
        insurancePolicyId: null,
        groupTitle: '',
        insurancePolicyProductId: null,
        label: '',
      }

    default:
      return base
  }
}

function bgcLikeConfigFromRow(
  row: CollectDataTemplateStepRow,
  approvalType: StepApprovalType,
  delayed: boolean,
): BackgroundCheckStepConfig | MvrUploadStepConfig {
  const metadata = stepMetadata(row)

  return {
    approvalType,
    delayed,
    optional: row.optional,
    provider: metadata.provider,
    yardstikPackage: typeof metadata.packageId === 'string' ? metadata.packageId : '',
    enableYardstik: Boolean(metadata.enableProvider),
    continueOnIncomplete: row.continueOnIncomplete ?? false,
    manualUpload: Boolean(metadata.enableManualUpload),
    expiresInDays: row.expiresInDays ?? 365,
  }
}

export function buildStepConfigFromApiRow(
  row: CollectDataTemplateStepRow,
  stepsConfig: CollectDataTemplateStepsConfig,
  type: CollectDataStepType,
): StepConfig {
  const approvalType = row.approvalType as StepApprovalType
  const delayed = stepsConfig.delayed
  const metadata = stepMetadata(row)

  switch (type) {
    case CollectDataStepType.BGC:
      return bgcLikeConfigFromRow(row, approvalType, delayed) as BackgroundCheckStepConfig

    case CollectDataStepType.MVR:
      return bgcLikeConfigFromRow(row, approvalType, delayed) as MvrUploadStepConfig

    case CollectDataStepType.PIICollection:
      return {
        approvalType,
        delayed,
        optional: row.optional,
        requireProofOfWorkAuthorization: Boolean(metadata.requireWorkAuthorizationProof),
        enforceSsnCollection: Boolean(metadata.enforceSsnCollection),
      }

    case CollectDataStepType.VehicleInsurance:
      return {
        approvalType,
        delayed,
        disableInsuranceUpload: Boolean(metadata.disableInsuranceUpload),
        requireExpirationDate: row.requireExpirationDate !== false,
      }

    case CollectDataStepType.InsuranceCoverage:
      return {
        approvalType,
        delayed,
        insurancePolicyId: stepsConfig.insurancePolicyId ?? null,
        groupTitle: typeof stepsConfig.title === 'string' ? stepsConfig.title : '',
        insurancePolicyProductId:
          typeof metadata.insurancePolicyProductId === 'string'
            ? metadata.insurancePolicyProductId
            : null,
        label: row.label,
      }

    case CollectDataStepType.VehicleRegistration:
      return {
        approvalType,
        delayed,
        requireExpirationDate: row.requireExpirationDate !== false,
      }

    case CollectDataStepType.Form: {
      const nameFromMeta = typeof metadata.title === 'string' ? metadata.title.trim() : ''

      const descriptionFromMeta =
        typeof metadata.description === 'string' ? metadata.description.trim() : ''

      return {
        label: row.label ?? '',
        optional: row.optional,
        approvalType,
        name: nameFromMeta,
        description: descriptionFromMeta,
        maxAttempts: row.maxAttempts,
        formItems: normalizeFormItemsFromApi(metadata.formItems),
      }
    }

    case CollectDataStepType.SignContract:
      return {
        approvalType,
        delayed,
        docusignTemplateId:
          typeof metadata.docusignTemplateId === 'string' ? metadata.docusignTemplateId : '',
      }

    case CollectDataStepType.LMSTraining:
      return {
        approvalType,
        delayed,
        lmsTrainingId: typeof metadata.lmsTrainingId === 'string' ? metadata.lmsTrainingId : '',
        lmsTrainingName:
          typeof metadata.lmsTrainingName === 'string' ? metadata.lmsTrainingName : '',
        expiresInDays: row.expiresInDays ?? 365,
        manualUpload: Boolean(metadata.enableManualUpload),
      }

    case CollectDataStepType.EIN:
      return {
        approvalType,
        delayed,
        optional: row.optional,
        maxAttempts: row.maxAttempts,
      }

    case CollectDataStepType.DOTNumber:
      return {
        approvalType,
        delayed,
        optional: row.optional,
        medicalCardRequirement:
          (metadata.medicalCardRequirement as MedicalCardRequirement | undefined) ??
          MedicalCardRequirement.Required,
      }

    case CollectDataStepType.MCNumber:
      return {
        approvalType,
        delayed,
        optional: row.optional,
      }

    default:
      return { approvalType, delayed }
  }
}

export function mapApiRowToEditorStep(
  row: CollectDataTemplateStepRow,
  stepsConfig: CollectDataTemplateStepsConfig,
): OnboardingTemplateStep {
  const type = row.type as CollectDataStepType
  const normalizedConfigId = normalizeCollectDataTemplateConfigId(stepsConfig.id)
  const configGroupId =
    normalizedConfigId !== '' ? normalizedConfigId : String(stepsConfig.id ?? '').trim()

  return {
    id: row.id,
    type,
    order: row.order,
    ...defaultOnboardingTemplateStepRowScalars(),
    retroactiveCollectRequired: row.retroactiveCollectRequired ?? false,
    ignoreMonitoring: row.ignoreMonitoring ?? false,
    recollectSameTypeSteps: row.recollectSameTypeSteps ?? false,
    skipRetroactiveCollectIfAnySameTypeCompleted:
      row.skipRetroactiveCollectIfAnySameTypeCompleted ?? false,
    collectDataTemplateStepsConfigId: configGroupId,
    collectDataTemplateStepLabel:
      typeof row.label === 'string' && row.label.trim() !== '' ? row.label : String(type),
    config: buildStepConfigFromApiRow(row, stepsConfig, type),
  }
}

export function getEditorStepsFromTemplate(template: OnboardingTemplate): OnboardingTemplateStep[] {
  const configs = [...(template.collectDataTemplateStepsConfigs ?? [])].sort(
    (a, b) => a.order - b.order,
  )
  const editorSteps: OnboardingTemplateStep[] = []

  for (const stepsConfig of configs) {
    const rows = filterDeprecatedCollectDataSteps(
      [...(stepsConfig.collectDataTemplateSteps ?? [])].sort((a, b) => a.order - b.order),
    )

    for (const row of rows) {
      editorSteps.push(mapApiRowToEditorStep(row, stepsConfig))
    }
  }

  return editorSteps
}

function buildMetadataFromEditorStep(
  step: OnboardingTemplateStep,
): CollectDataTemplateStepMetadataFields | undefined {
  const stepConfig = step.config

  switch (step.type) {
    case CollectDataStepType.BGC: {
      const backgroundCheckConfig = stepConfig as BackgroundCheckStepConfig

      return {
        provider: backgroundCheckConfig.provider,
        enableProvider: backgroundCheckConfig.enableYardstik,
        packageId: backgroundCheckConfig.yardstikPackage || undefined,
        enableManualUpload: backgroundCheckConfig.manualUpload,
      }
    }

    case CollectDataStepType.MVR: {
      const mvrUploadConfig = stepConfig as MvrUploadStepConfig

      return {
        provider: mvrUploadConfig.provider,
        enableProvider: mvrUploadConfig.enableYardstik,
        packageId: mvrUploadConfig.yardstikPackage || undefined,
        enableManualUpload: mvrUploadConfig.manualUpload,
      }
    }

    case CollectDataStepType.PIICollection: {
      const ssnAndAddressConfig = stepConfig as SsnAndAddressStepConfig

      return {
        requireWorkAuthorizationProof: ssnAndAddressConfig.requireProofOfWorkAuthorization,
        enforceSsnCollection: ssnAndAddressConfig.enforceSsnCollection,
      }
    }

    case CollectDataStepType.VehicleInsurance: {
      const vehicleInsuranceConfig = stepConfig as VehicleAndInsuranceStepConfig

      return {
        disableInsuranceUpload: vehicleInsuranceConfig.disableInsuranceUpload,
      }
    }

    case CollectDataStepType.SignContract: {
      const signContractConfig = stepConfig as SignContractStepConfig

      return {
        docusignTemplateId: signContractConfig.docusignTemplateId || undefined,
      }
    }

    case CollectDataStepType.LMSTraining: {
      const lmsTrainingConfig = stepConfig as LmsTrainingStepConfig

      return {
        lmsTrainingId: lmsTrainingConfig.lmsTrainingId || undefined,
        lmsTrainingName: lmsTrainingConfig.lmsTrainingName || undefined,
        enableManualUpload: lmsTrainingConfig.manualUpload,
      }
    }

    case CollectDataStepType.Form: {
      const formConfig = stepConfig as FormStepConfig

      return {
        title: formConfig.name.trim(),
        description: formConfig.description.trim(),
        formItems: formConfig.formItems as CollectDataFormTemplateItem[],
      }
    }

    case CollectDataStepType.DOTNumber: {
      const dotNumberConfig = stepConfig as DotNumberStepConfig

      return {
        medicalCardRequirement: dotNumberConfig.medicalCardRequirement,
      }
    }

    default:
      return undefined
  }
}

export function editorStepToApiRow(
  step: OnboardingTemplateStep,
  order: number,
  templateId: string,
  configId: string,
): CollectDataTemplateStepRow {
  const stepConfig = step.config
  const approvalType = (stepConfig as { approvalType: StepApprovalType }).approvalType

  let continueOnIncomplete = false
  let expiresInDays: number | null = null
  let requireExpirationDate: boolean | undefined
  let maxAttempts = 0
  let optional = false
  const retroactiveCollectRequired = step.retroactiveCollectRequired
  const ignoreMonitoring = step.ignoreMonitoring
  const recollectSameTypeSteps = step.recollectSameTypeSteps
  const skipRetroactiveCollectIfAnySameTypeCompleted =
    step.skipRetroactiveCollectIfAnySameTypeCompleted
  const trimmedPersistedLabel = step.collectDataTemplateStepLabel.trim()
  let label = trimmedPersistedLabel !== '' ? step.collectDataTemplateStepLabel : String(step.type)

  switch (step.type) {
    case CollectDataStepType.BGC: {
      const backgroundCheckConfig = stepConfig as BackgroundCheckStepConfig

      continueOnIncomplete = backgroundCheckConfig.continueOnIncomplete
      expiresInDays = backgroundCheckConfig.expiresInDays
      optional = backgroundCheckConfig.optional

      break
    }

    case CollectDataStepType.MVR: {
      const mvrUploadConfig = stepConfig as MvrUploadStepConfig

      continueOnIncomplete = mvrUploadConfig.continueOnIncomplete
      expiresInDays = mvrUploadConfig.expiresInDays
      optional = mvrUploadConfig.optional

      break
    }

    case CollectDataStepType.PIICollection: {
      const ssnAndAddressConfig = stepConfig as SsnAndAddressStepConfig

      optional = ssnAndAddressConfig.optional

      break
    }

    case CollectDataStepType.EIN: {
      const businessEntityConfig = stepConfig as BusinessEntityStepConfig

      optional = businessEntityConfig.optional
      maxAttempts = businessEntityConfig.maxAttempts

      break
    }

    case CollectDataStepType.DOTNumber: {
      const dotNumberConfig = stepConfig as DotNumberStepConfig

      optional = dotNumberConfig.optional

      break
    }

    case CollectDataStepType.MCNumber: {
      const mcNumberConfig = stepConfig as McNumberStepConfig

      optional = mcNumberConfig.optional

      break
    }

    case CollectDataStepType.LMSTraining: {
      const lmsTrainingConfig = stepConfig as LmsTrainingStepConfig

      expiresInDays = lmsTrainingConfig.expiresInDays

      break
    }

    case CollectDataStepType.Form: {
      const formConfig = stepConfig as FormStepConfig

      optional = formConfig.optional
      maxAttempts = formConfig.maxAttempts
      label = formConfig.label

      break
    }

    case CollectDataStepType.InsuranceCoverage: {
      const insuranceConfig = stepConfig as InsuranceStepConfig

      label = insuranceConfig.label

      break
    }

    case CollectDataStepType.VehicleInsurance: {
      const vehicleInsuranceConfig = stepConfig as VehicleAndInsuranceStepConfig

      requireExpirationDate = vehicleInsuranceConfig.disableInsuranceUpload
        ? vehicleInsuranceConfig.requireExpirationDate
        : true

      break
    }

    case CollectDataStepType.VehicleRegistration: {
      const vehicleRegistrationConfig = stepConfig as VehicleRegistrationStepConfig

      requireExpirationDate = vehicleRegistrationConfig.requireExpirationDate

      break
    }

    default:
      break
  }

  const metadata = buildMetadataFromEditorStep(step)
  let metadataPayload: CollectDataTemplateStepMetadataFields | undefined

  if (step.type === CollectDataStepType.InsuranceCoverage) {
    const insuranceConfig = stepConfig as InsuranceStepConfig
    const productId =
      typeof insuranceConfig.insurancePolicyProductId === 'string' &&
      insuranceConfig.insurancePolicyProductId.trim() !== ''
        ? insuranceConfig.insurancePolicyProductId.trim()
        : undefined

    metadataPayload = productId ? { insurancePolicyProductId: productId } : undefined
  } else {
    const metadataHasValues =
      metadata &&
      Object.keys(metadata).length > 0 &&
      Object.values(metadata).some((v) => v !== undefined && v !== '')

    metadataPayload = metadataHasValues ? metadata : undefined
  }

  return {
    id: step.id,
    type: step.type,
    label,
    metadata: metadataPayload,
    order,
    optional,
    retroactiveCollectRequired,
    expiresInDays: expiresInDays ?? undefined,
    requireExpirationDate,
    maxAttempts,
    approvalType,
    ignoreMonitoring,
    continueOnIncomplete,
    recollectSameTypeSteps,
    skipRetroactiveCollectIfAnySameTypeCompleted,
    collectDataTemplateId: templateId,
    collectDataTemplateStepsConfigId: normalizeCollectDataTemplateConfigId(configId) || configId,
  }
}
